<template>
  <section class="w-full md:w-1/2 xl:w-1/3 px-4">
    <card-post v-if="['post', 'page', 'bird', 'learn', 'article'].includes(item.type) && !isGift" :item="item" :one-column-on-tablets="oneColumnOnTablets"></card-post>

    <!-- The Gift Cards page uses this experience-style card too, in its own colours (.card-gift) -->
    <a v-else-if="['experience', 'programs', 'workshop'].includes(item.type) || isGift"
       :href="item.link"
       class="group relative h-full rounded-3xl rounded-br-none overflow-hidden md:transition-colors block"
       :class="[cardBackgroundClasses, { 'card-gift': isGift }]">
      <span
        v-if="item.categories || item.label"
        class="absolute top-0 right-0 z-10"
      >
        <span
          v-if="item.label || item.categories.length"
          class="inline-flex px-3 py-2 rounded-tr-3xl uppercase text-sm lg:text-mdx text-light font-medium tracking-wider"
          :class="categoryTagClasses"
        >
          <span v-html="item.label || item.categories[0].name" class="relative top-0.5"></span>
        </span>
      </span>

      <div class="relative block w-full"> 
        <v-lazy-image
          v-if="item.image"
          :class="'w-full select-none object-cover'"
          use-picture
          width="459"
          height="224"
          draggable="false"
          :alt="`${item.title} image`"
          :src="`${item.image.url}?tr=w-380,h-220`"
          :src-placeholder="`${$settings.theme_url}/assets/images/default-placeholder.png?tr=w-380,h-220`"
        >
          <!-- Two columns between 1024 and 1079px in the related blocks, so the card is about 480px wide -->
          <source
            media="(min-width: 1024px) and (max-width: 1079px)"
            sizes="calc(50vw - 32px)"
            :srcset="`${item.image.url}?tr=w-760,h-440 760w, ${item.image.url}?tr=w-1140,h-660 1140w`"
          />
          <source
            :srcset="
              `
              ${item.image.url}?tr=w-380,h-220,
              ${item.image.url}?tr=w-760,h-440 2x
            `
            "
          />
        </v-lazy-image>

        <img v-else class="w-full select-none object-cover" :src="`${$settings.theme_url}/assets/images/default-placeholder.png?tr=w-380,h-220`" alt="default placeholder" loading="lazy">

        <div class="absolute -bottom-0.5 left-0 z-10 w-full">
          <div class="-mr-1">
            <svg class="w-full md:transition-colors" :class="svgFillClasses" viewBox="0 0 384 31" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 1.51621V30.5001H384V3.35662C369.413 9.49075 342.427 20.3788 306.872 24.9794C273.676 29.2747 257.094 27.7398 236.308 27.2797C215.529 26.8198 174.496 20.3788 161.368 17.1584C148.239 13.938 91.8974 3.81651 68.9231 1.51621C50.5436 -0.324025 15.3162 0.749447 0 1.51621Z"></path>
            </svg>
          </div>
        </div>

        <div
          class="absolute bottom-0 left-6 z-30 flex justify-center items-center w-14 h-14 rounded-full"
          :class="experienceIconBackgroundClasses"
        >
          <svg v-if="isGift" width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M19.9609 1.75C18.9766 1.75 18.1016 2.24219 17.5547 3.0625L15.0938 7H15.75H20.125C21.5469 7 22.75 5.85156 22.75 4.375C22.75 2.95312 21.5469 1.75 20.125 1.75H19.9609ZM12.8516 7L10.3906 3.0625C9.84375 2.24219 8.96875 1.75 7.98438 1.75H7.875C6.39844 1.75 5.25 2.95312 5.25 4.375C5.25 5.85156 6.39844 7 7.875 7H12.25H12.8516ZM14 5.52344L16.0781 2.13281C16.9531 0.820312 18.375 0 19.9609 0H20.125C22.5312 0 24.5 1.96875 24.5 4.375C24.5 5.35938 24.1719 6.28906 23.625 7H25.375C26.7969 7 28 8.20312 28 9.625V13.125C28 14.2734 27.2344 15.2578 26.25 15.6406V15.75V24.5C26.25 26.4688 24.6641 28 22.75 28H5.25C3.28125 28 1.75 26.4688 1.75 24.5V15.75V15.6406C0.710938 15.2578 0 14.2734 0 13.125V9.625C0 8.20312 1.14844 7 2.625 7H4.375C3.82812 6.28906 3.5 5.35938 3.5 4.375C3.5 1.96875 5.41406 0 7.875 0H7.98438C9.57031 0 11.0469 0.820312 11.8672 2.13281L14 5.52344ZM7.875 8.75H2.625C2.13281 8.75 1.75 9.1875 1.75 9.625V13.125C1.75 13.6172 2.13281 14 2.625 14H3.5H13.125V8.75H12.25H7.875ZM14.875 8.75V14H24.5H25.375C25.8125 14 26.25 13.6172 26.25 13.125V9.625C26.25 9.1875 25.8125 8.75 25.375 8.75H20.125H15.75H14.875ZM13.125 15.75H3.5V24.5C3.5 25.4844 4.26562 26.25 5.25 26.25H13.125V15.75ZM14.875 26.25H22.75C23.6797 26.25 24.5 25.4844 24.5 24.5V15.75H14.875V26.25Z" class="fill-[var(--gift-icon)]"/>
          </svg>
          <experience-icon v-else :page-style="item.page_style" :inverted="isInverted"></experience-icon>
        </div>
      </div>

      <div class="relative px-6 pt-4 pb-6 md:transition-colors" :class="contentBackgroundClasses">
        <h3 class="mb-1 text-xl lg:text-2xl md:transition-colors" :class="titleTextClasses">
          <span v-html="item.title"></span>
        </h3>

        <p v-if="isGift && item.description" v-html="item.description" class="pr-14 text-base lg:text-lg font-regular md:transition-colors" :class="subtitleTextClasses"></p>
        
        <p v-if="hasTourTimeDetails" class="min-h-7 mb-3 text-base lg:text-lg font-regular md:transition-colors" :class="subtitleTextClasses">
          <span v-if="item.tour_time_details.info" v-html="item.tour_time_details.info"></span>
          <span v-if="item.tour_time_details.info && item.tour_time_details.duration"> | </span>
          <span v-if="item.tour_time_details.duration" v-html="item.tour_time_details.duration"></span>
        </p>

        <p v-if="item.school_program_duration" v-html="item.school_program_duration" class="text-base lg:text-lg font-regular md:transition-colors" :class="subtitleTextClasses"></p>

        <p v-if="item.school_program_location" class="flex items-center gap-3 mt-4 lg:mt-6 pr-14">
          <svg class="md:transition-colors" :class="iconFillClasses" width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.80859 17.5914C7.38672 18.1188 6.57812 18.1188 6.15625 17.5914C4.36328 15.3063 0.25 9.85706 0.25 6.76331C0.25 3.03674 3.27344 0.0133057 7 0.0133057C10.7266 0.0133057 13.75 3.03674 13.75 6.76331C13.75 9.85706 9.63672 15.3063 7.80859 17.5914ZM7 4.51331C6.19141 4.51331 5.45312 4.97034 5.03125 5.63831C4.64453 6.34143 4.64453 7.22034 5.03125 7.88831C5.45312 8.59143 6.19141 9.01331 7 9.01331C7.77344 9.01331 8.51172 8.59143 8.93359 7.88831C9.32031 7.22034 9.32031 6.34143 8.93359 5.63831C8.51172 4.97034 7.77344 4.51331 7 4.51331Z"></path>
          </svg>

          <span v-html="item.school_program_location" class="relative -top-px text-sm lg:text-base font-regular md:transition-colors" :class="locationTextClasses"></span>
        </p>

        <p v-if="item.school_program_tags" class="flex items-center gap-3 mt-4 lg:mt-6 pr-14">
          <svg class="md:transition-colors" :class="iconFillClasses" width="26" height="20" viewBox="0 0 26 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.19412 0.666687C8.77745 0.666687 10.3168 0.958354 11.4603 1.20835C12.0321 1.33335 12.4719 1.45835 12.8237 1.58335C12.8677 1.58335 12.9117 1.62502 12.9997 1.62502C13.0437 1.62502 13.0876 1.58335 13.1316 1.58335C13.4835 1.45835 13.9233 1.33335 14.495 1.20835C15.6386 0.958354 17.1779 0.666687 18.8052 0.666687C21.2242 0.666687 23.5112 1.29169 24.4349 1.58335C25.1825 1.79169 25.6663 2.45835 25.6663 3.16669V16.125C25.6663 17.25 24.5228 18 23.5112 17.8334C22.6756 17.6667 21.2242 17.5 19.1571 17.5C16.5622 17.5 14.8469 18.5417 14.2312 18.9584C13.9233 19.1667 13.4835 19.3334 12.9557 19.3334C12.5159 19.3334 12.12 19.2084 11.7682 19C11.1085 18.5834 9.21727 17.5 6.84227 17.5C4.81912 17.5 3.27977 17.7084 2.44412 17.8334C1.38856 18 0.333008 17.25 0.333008 16.1667V3.16669C0.333008 2.45835 0.772823 1.79169 1.52051 1.58335C2.44412 1.29169 4.73116 0.666687 7.19412 0.666687ZM11.9441 3.41669C11.6802 3.33335 11.3284 3.25002 10.9325 3.16669C9.92097 2.91669 8.55754 2.66669 7.19412 2.66669C5.25893 2.66669 3.41171 3.12502 2.44412 3.41669V15.8334C3.41171 15.6667 4.95106 15.5 6.84227 15.5C8.95338 15.5 10.8006 16.2084 11.9441 16.75V3.41669ZM14.0552 16.7084C15.1548 16.125 16.87 15.5 19.1571 15.5C21.1362 15.5 22.5876 15.6667 23.5552 15.7917V3.41669C22.5437 3.12502 20.6964 2.66669 18.8052 2.66669C17.3978 2.66669 16.0344 2.91669 15.0228 3.16669C14.627 3.25002 14.3191 3.33335 14.0552 3.41669V16.7084Z" />
          </svg>

          <span v-html="item.school_program_tags" class="text-sm lg:text-base font-regular md:transition-colors" :class="locationTextClasses"></span>
        </p>

        <div v-if="item.short_address" class="flex items-center gap-3 max-w-52 lg:max-w-64 text-sm lg:text-base font-regular md:transition-colors" :class="locationTextClasses">
          <svg class="md:transition-colors" :class="iconFillClasses" width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.80859 17.5914C7.38672 18.1188 6.57812 18.1188 6.15625 17.5914C4.36328 15.3063 0.25 9.85706 0.25 6.76331C0.25 3.03674 3.27344 0.0133057 7 0.0133057C10.7266 0.0133057 13.75 3.03674 13.75 6.76331C13.75 9.85706 9.63672 15.3063 7.80859 17.5914ZM7 4.51331C6.19141 4.51331 5.45312 4.97034 5.03125 5.63831C4.64453 6.34143 4.64453 7.22034 5.03125 7.88831C5.45312 8.59143 6.19141 9.01331 7 9.01331C7.77344 9.01331 8.51172 8.59143 8.93359 7.88831C9.32031 7.22034 9.32031 6.34143 8.93359 5.63831C8.51172 4.97034 7.77344 4.51331 7 4.51331Z"></path>
          </svg>

          {{ item.short_address }}
        </div>
      </div>

      <div class="absolute right-0 bottom-0 flex justify-center items-center w-22.75 h-23">
        <svg class="absolute -bottom-0.5 right-[-1px] md:transition-colors" :class="arrowBackgroundClasses" width="91" height="92" viewBox="0 0 91 92" xmlns="http://www.w3.org/2000/svg">
          <clipPath :id="`clipCircle-${item.id}`">
            <circle cx="59" cy="59" r="22" />
          </clipPath>

          <path :id="`corner-${item.id}`" d="M84.5732 13.6802C89.5778 9.16883 90.9672 3.25002 91.0001 0.500244L91.0001 18.9901H72.5103C74.1582 18.9901 79.6293 18.1367 84.5732 13.6802ZM12.0614 86.189C17.0661 81.6776 18.4554 75.7588 18.4884 73.009V91.4989L-0.00146484 91.4989C1.64648 91.4989 7.11762 90.6455 12.0614 86.189ZM91.0001 91.4999V18.9901L55.9411 18.9906C35.2579 18.9906 18.4909 35.7576 18.4909 56.4408L18.4909 91.4999H91.0001Z"></path>

          <use :href="`#corner-${item.id}`" :clip-path="`url(#clipCircle-${item.id})`" :class="arrowCircleClasses" />

          <path d="M67.364 59.3777L61.114 65.6277C60.6453 66.1355 59.825 66.1355 59.3562 65.6277C58.8484 65.1589 58.8484 64.3386 59.3562 63.8699L63.4578 59.7292H51.5046C50.8015 59.7292 50.2546 59.1824 50.2546 58.4792C50.2546 57.8152 50.8015 57.2292 51.5046 57.2292H63.4578L59.3562 53.1277C58.8484 52.6589 58.8484 51.8386 59.3562 51.3699C
59.825 50.8621 60.6453 50.8621 61.114 51.3699L67.364 57.6199C67.8718 58.0886 67.8718 58.9089 67.364 59.3777Z" fill="#F4F1EB" :class="{ 'fill-[var(--gift-arrow)]': isGift }"/>
        </svg>
      </div>
    </a>
  </section>
</template>

<script>
import CardPost from "./CardPost.vue"
import ExperienceIcon from "./ExperienceIcon.vue"
import VLazyImage from "./../plugins/v-lazy-image/dist/v-lazy-image";
import dayjs from "dayjs"
import LinkMixin from "./../mixins/link"
import PriceMixin from "./../mixins/price"
import HoursMixin from "./../mixins/hours"

export default {
  name: "Card",
  components: {
    VLazyImage,
    ExperienceIcon,
    CardPost,
  },
  mixins: [LinkMixin, PriceMixin, HoursMixin],
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    search: {
      type: Object,
      default: () => ({}),
    },
    order: {
      type: [Object, Boolean],
      default: false,
    },
    mapToggle: {
      type: Boolean,
      default: true,
    },
    showTags: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: "content",
    },
    // Passed to CardPost: false in the related blocks, which are two columns from 640px
    oneColumnOnTablets: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {}
  },
  computed: {
    date() {
      return this.item?.date?.event_date ? dayjs(this.item.date.event_date).format("DD.MM.YYYY") : false
    },
    hasTourTimeDetails() {
      return this.item.tour_time_details && (this.item.tour_time_details.info || this.item.tour_time_details.duration);
    },
    isGift() {
      return this.item.icon === 'gift';
    },
    isInverted() {
      return this.item.inverted_colours === true || this.item.inverted_colours === 1 || this.item.inverted_colours === '1';
    },
    cardBackgroundClasses() {
      if (this.isGift) {
        return 'bg-[color:var(--gift-bg)] hover:bg-[color:var(--gift-hover)] active:bg-[color:var(--gift-hover)] focus:bg-[color:var(--gift-hover)]';
      }
      if (this.isInverted) {
        // Inverted: Start with colored background, hover to light
        return `bg-${this.item.page_style}-secondary hover:bg-light active:bg-light focus:bg-light`;
      } else {
        // Normal: Start with light background, hover to colored
        return `bg-light hover:bg-${this.item.page_style}-secondary active:bg-${this.item.page_style}-secondary focus:bg-${this.item.page_style}-secondary`;
      }
    },
    contentBackgroundClasses() {
      if (this.isGift) {
        return 'group-hover:bg-[color:var(--gift-hover)] group-active:bg-[color:var(--gift-hover)] group-focus:bg-[color:var(--gift-hover)]';
      }
      if (this.isInverted) {
        // Inverted: Start with colored background, hover to light
        return `group-hover:bg-light group-active:bg-light group-focus:bg-light`;
      } else {
        // Normal: Start with light background, hover to colored
        return `group-hover:bg-${this.item.page_style}-secondary group-active:bg-${this.item.page_style}-secondary group-focus:bg-${this.item.page_style}-secondary`;
      }
    },
    svgFillClasses() {
      if (this.isGift) {
        return 'fill-[var(--gift-bg)] group-hover:fill-[var(--gift-hover)] group-active:fill-[var(--gift-hover)] group-focus:fill-[var(--gift-hover)]';
      }
      if (this.isInverted) {
        // Inverted: Start with colored fill, hover to light
        return `fill-${this.item.page_style}-secondary group-hover:fill-light group-active:fill-light group-focus:fill-light`;
      } else {
        // Normal: Start with light fill, hover to colored
        return `fill-light group-hover:fill-${this.item.page_style}-secondary group-active:fill-${this.item.page_style}-secondary group-focus:fill-${this.item.page_style}-secondary`;
      }
    },
    titleTextClasses() {
      if (this.isGift) {
        return 'text-[color:var(--gift-text)] group-hover:text-[color:var(--gift-hover-text)] group-active:text-[color:var(--gift-hover-text)] group-focus:text-[color:var(--gift-hover-text)]';
      }
      if (this.isInverted) {
        // Inverted: Start with white text, hover to dark
        return 'text-white group-hover:text-dark group-active:text-dark group-focus:text-dark';
      } else {
        // Normal: Start with dark text, hover to white
        return 'text-dark group-hover:text-white group-active:text-white group-focus:text-white';
      }
    },
    subtitleTextClasses() {
      if (this.isGift) {
        return 'text-[color:var(--gift-text)] group-hover:text-[color:var(--gift-hover-text)] group-active:text-[color:var(--gift-hover-text)] group-focus:text-[color:var(--gift-hover-text)]';
      }
      if (this.isInverted) {
        // Inverted: Start with white text, hover to dark
        return 'text-white group-hover:text-dark group-active:text-dark group-focus:text-dark';
      } else {
        // Normal: Start with dark text, hover to white
        return 'text-dark group-hover:text-white group-active:text-white group-focus:text-white';
      }
    },
    locationTextClasses() {
      if (this.isInverted) {
        // Inverted: Start with white text, hover to grey
        return 'text-white group-hover:text-grey group-active:text-grey group-focus:text-grey';
      } else {
        // Normal: Start with grey text, hover to white
        return 'text-grey group-hover:text-white group-active:text-white group-focus:text-white';
      }
    },
    iconFillClasses() {
      if (this.isInverted) {
        // Inverted: Location/other icons start primary-tone, hover to secondary
        return `fill-${this.item.page_style}-primary-tone group-hover:fill-${this.item.page_style}-secondary group-active:fill-${this.item.page_style}-secondary group-focus:fill-${this.item.page_style}-secondary`;
      } else {
        // Normal: Start with colored fill, hover to white
        return `fill-${this.item.page_style}-secondary group-hover:fill-white group-active:fill-white group-focus:fill-white`;
      }
    },
    categoryTagClasses() {
      if (this.isGift) {
        return 'bg-[color:var(--gift-accent)] !text-[color:var(--gift-accent-text)]';
      }
      if (this.isInverted) {
        // Inverted: Start with tertiary background, no hover change
        return `bg-${this.item.page_style}-tertiary`;
      } else {
        // Normal: Start with secondary background
        return `bg-${this.item.page_style}-secondary`;
      }
    },
    arrowBackgroundClasses() {
      if (this.isInverted) {
        // When inverted: keep shape white on hover/active/focus
        return 'fill-white';
      } else {
        // When not inverted: stay white always
        return 'fill-white';
      }
    },
    arrowCircleClasses() {
      if (this.isGift) {
        return 'fill-[var(--gift-arrow-bg)]';
      }
      if (this.isInverted) {
        // Inverted: Circle background is always tertiary, never changes
        return `fill-${this.item.page_style}-tertiary`;
      } else {
        // Normal: Circle background follows the default pattern
        return this.item.page_style === 'default' ? 'fill-default' : `fill-${this.item.page_style}-secondary`;
      }
    },
    experienceIconBackgroundClasses() {
      if (this.isGift) {
        return 'bg-[color:var(--gift-icon-bg)]';
      }
      if (this.isInverted) {
        // Inverted: Background is always light, no hover effect
        return `bg-light`;
      } else {
        // Normal: Always primary background
        return `bg-${this.item.page_style}-primary`;
      }
    }
  },
  methods: {
    activate() {
      this.$emit("activate", this.item)
    },
    click() {
      this.$emit("click", this.item)
    },
  },
  filters: {
    dateRange: function (arr) {
      let from = dayjs(arr[0]).format("D.MMMM.YYYY")
      let to = dayjs(arr[arr.length - 1]).format("D.MMMM.YYYY")
      let fromArr = from.split(".")
      let toArr = to.split(".")
      let buildReadableDate = (arr) => {
        let day = parseInt(arr[0])
        let first = [1, 21, 31]
        let second = [2, 22]
        let third = [3, 23]
        let suffix = "th"

        if (first.includes(day)) {
          suffix = "st"
        }

        if (second.includes(day)) {
          suffix = "nd"
        }

        if (third.includes(day)) {
          suffix = "rd"
        }

        day += suffix

        let date = `${day} ${arr[1]} ${arr[2]}`

        return date
      }

      let dateRange = `From ${buildReadableDate(fromArr)} to ${buildReadableDate(toArr)}`

      return dateRange
    },
  },
}
</script>
